import { cn } from "@/utils/cn";
import { BattleEncounter } from "./gameTypes";
import { OrcamentoAnaliseSolicitacaoData, OrcamentoAnaliseVeredito } from "@/services/orcamento";

interface GameBattleViewProps {
  encounter: BattleEncounter;
  data: OrcamentoAnaliseSolicitacaoData | null;
  loading: boolean;
  error: string | null;
  onBack: () => void;
}

export function GameBattleView({
  encounter,
  data,
  loading,
  error,
  onBack,
}: GameBattleViewProps) {
  const pixelFont: React.CSSProperties = { fontFamily: "'Press Start 2P', monospace" };

  return (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Battle header */}
      <div className="flex-shrink-0 border-b border-red-900 bg-red-950/30 p-3 flex items-center gap-3">
        <button
          onClick={onBack}
          className="text-gray-500 hover:text-white text-[8px] font-mono border border-gray-700 px-2 py-1 hover:border-gray-500 transition-colors"
        >
          ← VOLTAR
        </button>
        <div className="flex-1 text-center">
          <span className="text-red-400 text-[8px]" style={pixelFont}>
            ⚔ BATALHA — SC #{encounter.purchaseRequestId} ⚔
          </span>
        </div>
      </div>

      <div className="flex-1 overflow-y-auto p-4">
        {/* Loading */}
        {loading && (
          <div className="flex flex-col items-center justify-center h-48 gap-4">
            <div className="text-4xl animate-bounce">⚔️</div>
            <span className="text-yellow-400 text-[8px]" style={pixelFont}>
              ANÁLISE INICIANDO…
            </span>
            <div className="flex gap-1">
              {Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className="w-2 h-2 bg-yellow-500 animate-pulse"
                  style={{ animationDelay: `${i * 200}ms` }}
                />
              ))}
            </div>
          </div>
        )}

        {/* Error */}
        {error && !loading && (
          <div className="flex flex-col items-center justify-center h-48 gap-4">
            <div className="text-4xl">💀</div>
            <span className="text-red-400 text-[8px]" style={pixelFont}>
              ERRO NA ANÁLISE
            </span>
            <p className="text-gray-500 text-[9px] font-mono text-center max-w-xs">{error}</p>
            <button
              onClick={onBack}
              className="mt-2 border border-gray-600 text-gray-400 text-[8px] px-3 py-1.5 hover:border-gray-400 transition-colors"
              style={pixelFont}
            >
              RECUAR
            </button>
          </div>
        )}

        {/* Battle results */}
        {data && !loading && (
          <BattleResults data={data} onBack={onBack} pixelFont={pixelFont} />
        )}
      </div>
    </div>
  );
}

function BattleResults({
  data,
  onBack,
  pixelFont,
}: {
  data: OrcamentoAnaliseSolicitacaoData;
  onBack: () => void;
  pixelFont: React.CSSProperties;
}) {
  const { resumo } = data;
  const veredito = resumo.veredito;

  const vereditoConfig: Record<
    OrcamentoAnaliseVeredito,
    { emoji: string; label: string; color: string; bg: string; border: string }
  > = {
    ok: {
      emoji: "🏆",
      label: "VITÓRIA!",
      color: "text-emerald-400",
      bg: "bg-emerald-950/40",
      border: "border-emerald-600",
    },
    atencao: {
      emoji: "⚠️",
      label: "ATENÇÃO",
      color: "text-yellow-400",
      bg: "bg-yellow-950/40",
      border: "border-yellow-600",
    },
    critico: {
      emoji: "💥",
      label: "CRÍTICO!",
      color: "text-red-400",
      bg: "bg-red-950/40",
      border: "border-red-600",
    },
  };

  const vc = vereditoConfig[veredito];

  return (
    <div className="max-w-2xl mx-auto space-y-3">
      {/* Veredito banner */}
      <div className={cn("border-2 p-4 text-center", vc.border, vc.bg)}>
        <div className="text-3xl mb-2">{vc.emoji}</div>
        <div className={cn("text-[11px] font-bold mb-1", vc.color)} style={pixelFont}>
          {vc.label}
        </div>
        <div className="flex justify-center gap-4 mt-3">
          <div className="text-center">
            <div className="text-red-400 text-[10px] font-bold" style={pixelFont}>
              {resumo.linhasComExcesso}
            </div>
            <div className="text-gray-600 text-[7px] font-mono">excedidos</div>
          </div>
          <div className="text-center">
            <div className="text-yellow-400 text-[10px] font-bold" style={pixelFont}>
              {resumo.linhasSemOrcamento}
            </div>
            <div className="text-gray-600 text-[7px] font-mono">sem orçamento</div>
          </div>
          <div className="text-center">
            <div className="text-emerald-400 text-[10px] font-bold" style={pixelFont}>
              {data.items.length}
            </div>
            <div className="text-gray-600 text-[7px] font-mono">itens</div>
          </div>
        </div>
      </div>

      {/* Context */}
      <div className="border border-gray-800 p-3">
        <div className="text-gray-500 text-[7px] mb-2" style={pixelFont}>ARENA</div>
        <div className="space-y-1">
          <InfoRow label="Obra" value={data.buildingName || data.buildingId} />
          <InfoRow label="Centro de Custo" value={data.costCenter.name || data.costCenter.id} />
        </div>
      </div>

      {/* Items */}
      <div className="border border-gray-800 p-3">
        <div className="text-gray-500 text-[7px] mb-3" style={pixelFont}>
          TURNOS DA BATALHA — ITENS DA SC
        </div>
        <div className="space-y-3">
          {data.items.map((item, idx) => (
            <ItemTurn key={item.itemNumber || idx} item={item} pixelFont={pixelFont} />
          ))}
        </div>
      </div>

      {/* Messages */}
      {resumo.mensagens.length > 0 && (
        <div className="border border-gray-700 p-3 bg-gray-900/40">
          <div className="text-gray-500 text-[7px] mb-2" style={pixelFont}>LOG DE BATALHA</div>
          {resumo.mensagens.map((msg, i) => (
            <p key={i} className="text-gray-400 text-[8px] font-mono mb-1">
              › {msg}
            </p>
          ))}
        </div>
      )}

      {/* Back button */}
      <div className="flex justify-center pt-2">
        <button
          onClick={onBack}
          className="border border-gray-600 text-gray-400 text-[8px] px-5 py-2.5 hover:border-emerald-600 hover:text-emerald-400 transition-colors"
          style={pixelFont}
        >
          ← VOLTAR À LISTA
        </button>
      </div>
    </div>
  );
}

function ItemTurn({
  item,
  pixelFont,
}: {
  item: OrcamentoAnaliseSolicitacaoData["items"][0];
  pixelFont: React.CSSProperties;
}) {
  const hasExcesso = item.analysisRows.some((r) => r.excede);
  const hasSemOrc = item.analysisRows.some((r) => r.semOrcamentoNestaUc);

  const statusConfig = hasExcesso
    ? { color: "text-red-400", border: "border-red-800", bg: "bg-red-950/20", label: "CRÍTICO 💥" }
    : hasSemOrc
      ? { color: "text-yellow-400", border: "border-yellow-800", bg: "bg-yellow-950/20", label: "ATENÇÃO ⚠" }
      : { color: "text-emerald-400", border: "border-emerald-900", bg: "bg-emerald-950/20", label: "OK ✓" };

  return (
    <div className={cn("border p-3", statusConfig.border, statusConfig.bg)}>
      {/* Item header */}
      <div className="flex items-start justify-between gap-2 mb-2">
        <div>
          <span className="text-gray-600 text-[7px] font-mono">#{item.itemNumber} </span>
          <span className="text-white text-[8px] font-mono">{item.description}</span>
        </div>
        <span className={cn("text-[7px] flex-shrink-0", statusConfig.color)} style={pixelFont}>
          {statusConfig.label}
        </span>
      </div>

      <div className="text-gray-600 text-[7px] font-mono mb-2">
        Qty: {item.quantity} {item.unit}
      </div>

      {/* Analysis rows per UC */}
      {item.analysisRows.slice(0, 4).map((row, i) => (
        <UcRow key={i} row={row} pixelFont={pixelFont} />
      ))}
      {item.analysisRows.length > 4 && (
        <div className="text-gray-700 text-[7px] font-mono mt-1">
          + {item.analysisRows.length - 4} UCs…
        </div>
      )}
    </div>
  );
}

function UcRow({
  row,
  pixelFont,
}: {
  row: OrcamentoAnaliseSolicitacaoData["items"][0]["analysisRows"][0];
  pixelFont: React.CSSProperties;
}) {
  const pct = row.pctApos ?? 0;
  const barColor =
    row.excede ? "bg-red-500" : pct >= 90 ? "bg-yellow-500" : "bg-emerald-500";
  const textColor =
    row.excede ? "text-red-400" : row.semOrcamentoNestaUc ? "text-yellow-400" : "text-emerald-400";

  return (
    <div className="mt-1.5 pl-2 border-l border-gray-800">
      <div className="flex items-center justify-between gap-2 mb-0.5">
        <span className="text-gray-500 text-[7px] font-mono truncate">{row.ucName}</span>
        <span className={cn("text-[7px] flex-shrink-0", textColor)} style={pixelFont}>
          {row.semOrcamentoNestaUc
            ? "SEM ORC."
            : row.excede
              ? `${row.pctApos?.toFixed(0) ?? "—"}% ↑`
              : `${row.pctApos?.toFixed(0) ?? "—"}%`}
        </span>
      </div>
      {!row.semOrcamentoNestaUc && row.orcado > 0 && (
        <div className="h-1 bg-gray-800 overflow-hidden">
          <div
            className={cn("h-full transition-all", barColor)}
            style={{ width: `${Math.min(100, pct)}%` }}
          />
        </div>
      )}
      <div className="flex gap-3 mt-0.5">
        <span className="text-gray-700 text-[6px] font-mono">
          ORC: {Number(row.orcado).toFixed(1)} {" "}
          REAL: {Number(row.realizado).toFixed(1)} {" "}
          SIM: {Number(row.simulado).toFixed(1)}
        </span>
      </div>
    </div>
  );
}

function InfoRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between items-start gap-2">
      <span className="text-gray-600 text-[8px] font-mono flex-shrink-0">{label}</span>
      <span className="text-gray-300 text-[8px] font-mono text-right">{value || "—"}</span>
    </div>
  );
}
